import {
	ASSETS_CF_TYPE,
	CASCADING_SELECT_CF_TYPE,
	DATETIME_CF_TYPE,
	DATE_CF_TYPE,
	GROUP_CF_TYPE,
	LABELS_CF_TYPE,
	LABELS_TYPE as LABELS_FIELD_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	MULTI_GROUP_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	MULTI_VERSION_CF_TYPE,
	NUMBER_CF_TYPE,
	PEOPLE_CF_TYPE,
	PRIORITY_TYPE as PRIORITY_FIELD_TYPE,
	PROJECT_PICKER_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	SELECT_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	TEXT_CF_TYPE,
	VERSION_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	APPROVALS_CF_TYPE,
	DESCRIPTION_FIELD_TYPE,
	ENVIRONMENT_FIELD_TYPE,
	TEXT_AREA_CF_TYPE,
} from '@atlassian/jira-project-settings-apps-common/src/consts.tsx';
import type { FieldCard } from '../../types.tsx';
import {
	ApprovalsFieldConfiguration,
	isExpandable as isApprovalsFieldExpandable,
} from './field-configuration/approvals-data/index.tsx';
import {
	AssetTypeFieldConfiguration,
	isExpandable as isAssetTypePickerFieldExpandable,
} from './field-configuration/asset-type-picker/index.tsx';
import { isExpandable as isCascadingDropdownExpandable } from './field-configuration/cascading-dropdown/index.tsx';
import { CascadingDropdown } from './field-configuration/cascading-dropdown/view.tsx';
import { isExpandable as isCheckboxFieldExpandable } from './field-configuration/checkboxes/index.tsx';
import { CheckboxFieldConfiguration } from './field-configuration/checkboxes/view.tsx';
import { CurrentDateTimeDefaultValue } from './field-configuration/current-date-time/view.tsx';
import { isExpandable as isDateTimeFieldExpandable } from './field-configuration/date-time/index.tsx';
import { isExpandable as isDropdownFieldExpandable } from './field-configuration/dropdown/index.tsx';
import { DropdownFieldConfiguration } from './field-configuration/dropdown/view.tsx';
import { isExpandable as isGroupFieldExpandable } from './field-configuration/group-picker/index.tsx';
import {
	MultiGroupFieldConfiguration,
	SingleGroupFieldConfiguration,
} from './field-configuration/group-picker/view.tsx';
import { isExpandable as isLabelsFieldExpandable } from './field-configuration/labels/index.tsx';
import { LabelsFieldConfiguration } from './field-configuration/labels/view.tsx';
import {
	MultilineFieldConfiguration,
	isExpandable as isMultilineFieldExpandable,
} from './field-configuration/multiline/index.tsx';
import { isExpandable as isNumberFieldExpandable } from './field-configuration/number/index.tsx';
import { NumberFieldConfiguration } from './field-configuration/number/view.tsx';
import { isExpandable as isPeopleFieldConfigurationExpandable } from './field-configuration/people/index.tsx';
import { PeopleFieldConfiguration } from './field-configuration/people/view.tsx';
import { isExpandable as isPriorityFieldExpandable } from './field-configuration/priority/index.tsx';
import { PriorityFieldConfiguration } from './field-configuration/priority/view.tsx';
import { isExpandable as isProjectPickerFieldExpandable } from './field-configuration/project-picker/index.tsx';
import ProjectPickerFieldConfiguration from './field-configuration/project-picker/main.tsx';
import { isExpandable as isTextFieldExpandable } from './field-configuration/text/index.tsx';
import { TextFieldConfiguration } from './field-configuration/text/view.tsx';
import { isExpandable as isVersionFieldExpandable } from './field-configuration/versions/index.tsx';
import { MultiVersionFieldConfiguration } from './field-configuration/versions/multi/index.tsx';
import { VersionFieldConfiguration } from './field-configuration/versions/single/index.tsx';

const unsupportedFieldCard: FieldCard = {
	isExpandable: () => false,
};

/**
 * @deprecated write your own getter.
 *
 * This is the basic, or default field card getter that we use in platform.
 * You can reuse it or extend it but be aware that this isn't probably a good idea because
 * we will follow what JSW TMP needs first.
 *
 * This mostly exists because in the past it used to be all platform code.
 * We will refactor step by step and once all products have their own individual definitions
 * then we remove this "default" getter.
 */
export const getFieldCard = (fieldType: string): FieldCard => {
	switch (fieldType) {
		case APPROVALS_CF_TYPE: {
			return {
				isFieldDescriptionApplicable: false,
				ConfigurationComponent: ApprovalsFieldConfiguration,
				isExpandable: isApprovalsFieldExpandable,
			};
		}
		case PEOPLE_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: PeopleFieldConfiguration,
				isExpandable: isPeopleFieldConfigurationExpandable,
			};
		case SELECT_CF_TYPE:
		case RADIO_BUTTONS_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: DropdownFieldConfiguration,
				isExpandable: isDropdownFieldExpandable,
			};
		case CASCADING_SELECT_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: CascadingDropdown,
				isExpandable: isCascadingDropdownExpandable,
			};
		case MULTI_CHECKBOXES_CF_TYPE:
		case MULTI_SELECT_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: CheckboxFieldConfiguration,
				isExpandable: isCheckboxFieldExpandable,
			};
		case PRIORITY_FIELD_TYPE:
			return {
				isFieldDescriptionApplicable: false,
				isExpandable: isPriorityFieldExpandable,
				ConfigurationComponent: PriorityFieldConfiguration,
			};
		case TEXT_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: TextFieldConfiguration,
				isExpandable: isTextFieldExpandable,
			};
		case TEXT_AREA_CF_TYPE:
		case DESCRIPTION_FIELD_TYPE:
		case ENVIRONMENT_FIELD_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: MultilineFieldConfiguration,
				isExpandable: isMultilineFieldExpandable,
			};
		case LABELS_FIELD_TYPE:
		case LABELS_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: LabelsFieldConfiguration,
				isExpandable: isLabelsFieldExpandable,
			};
		/**
		 * Date CF does not support default values because of a bug that we found in 2019
		 * where roadmaps code was having "issues with their Start Date field".
		 * The details have been lost over the years, and it's entirely possible
		 * that we can re-enable support for this default today.
		 *
		 * Also backend will actively refuse to set a default value for date fields
		 * so make sure to fix that before re-enabling Date default values for JSW TMP here.
		 *
		 * We however do offer several "Default value" components for use with Date and Datetime so feel free to use these
		 * - just make sure that the saving actually works.
		 *
		 * See https://stash.atlassian.com/projects/JIRACLOUD/repos/jira-frontend/pull-requests/15347/overview
		 */
		case DATE_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: () => null,
				isExpandable: () => false,
			};
		case DATETIME_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: CurrentDateTimeDefaultValue,
				isExpandable: isDateTimeFieldExpandable,
			};
		case NUMBER_CF_TYPE:
		case STORY_POINT_ESTIMATE_CF_TYPE:
			// The story points CF is simply an extension of the number CF type, just uniquely named
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: NumberFieldConfiguration,
				isExpandable: isNumberFieldExpandable,
			};
		case ASSETS_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: AssetTypeFieldConfiguration,
				isExpandable: isAssetTypePickerFieldExpandable,
			};
		case GROUP_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: SingleGroupFieldConfiguration,
				isExpandable: isGroupFieldExpandable,
			};
		case MULTI_GROUP_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: MultiGroupFieldConfiguration,
				isExpandable: isGroupFieldExpandable,
			};
		case VERSION_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: VersionFieldConfiguration,
				isExpandable: isVersionFieldExpandable,
			};
		case MULTI_VERSION_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: MultiVersionFieldConfiguration,
				isExpandable: isVersionFieldExpandable,
			};
		case PROJECT_PICKER_CF_TYPE:
			return {
				isFieldDescriptionApplicable: true,
				ConfigurationComponent: ProjectPickerFieldConfiguration,
				isExpandable: isProjectPickerFieldExpandable,
			};
		default:
			return unsupportedFieldCard;
	}
};
